/**
 * The error boundary around the router (bata-platform#686, ADR 9063).
 *
 * Before it, a screen that threw while rendering unmounted the whole tree: the rail, the account
 * menu, the sign-out — everything — leaving a white page. A person in that state had no way to
 * leave the page they could not use, let alone to say what happened.
 *
 * It sits INSIDE the chrome, around the router only, so the rail and the identity menu survive a
 * screen crash. It says so briefly, offers a reload, and a «Report a problem» button that opens
 * the report form already filled in: a malfunction, the error's message as the description, and
 * the route the person is on (the provider's). It is reset by navigation — the `resetKey` is the
 * path — so choosing another screen in the rail leaves the crash behind.
 *
 * A render error only. An event handler's or a promise's failure is not caught by a boundary, and
 * the data screens already state their own read failures (`ProblemNotice`).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareWarning } from 'lucide-react';

import { Button, EmptyState } from '../../primitives';

import { useFeedback } from './FeedbackProvider';

export interface ScreenErrorBoundaryProps {
  readonly children: ReactNode;
  /** The error is cleared when this changes — the current path, so another screen starts clean. */
  readonly resetKey: string;
}

interface State {
  readonly error: Error | null;
}

export class ScreenErrorBoundary extends Component<ScreenErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // React logs a caught render error in development only; a production crash would otherwise
    // leave nothing in the console for the person to copy or for the error tracker to read.
    console.error(error, info.componentStack);
  }

  override componentDidUpdate(previous: ScreenErrorBoundaryProps): void {
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render(): ReactNode {
    return this.state.error === null ? this.props.children : <CrashNotice error={this.state.error} />;
  }
}

/** What the content area shows in place of a screen that threw. */
function CrashNotice({ error }: { readonly error: Error }): React.JSX.Element {
  const { t } = useTranslation();
  const feedback = useFeedback();
  return (
    <div className="flex flex-col items-start gap-2" data-screen-crash="">
      <EmptyState
        variant="error"
        titleKey="feedback.crash.title"
        bodyKey="feedback.crash.body"
        action={{ labelKey: 'feedback.crash.reload', onClick: () => window.location.reload() }}
      />
      {feedback !== null && (
        <Button
          variant="secondary"
          icon={<MessageSquareWarning size={14} aria-hidden />}
          onClick={() => feedback.open({
            kind: 'incident',
            description: t('feedback.crash.prefill', { message: error.message === '' ? error.name : error.message }),
          })}
        >
          {t('feedback.report.open')}
        </Button>
      )}
    </div>
  );
}
